if (!customElements.get('finch-customer-slider')) {
  class FinchCustomerSlider extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.track = this.querySelector('[data-fcs-track]');
      this.cards = Array.from(this.querySelectorAll('[data-fcs-card]'));
      this.previousButton = this.querySelector('[data-fcs-prev]');
      this.nextButton = this.querySelector('[data-fcs-next]');
      this.progress = this.querySelector('[data-fcs-progress]');
      this.status = this.querySelector('[data-fcs-status]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.scrollFrame = null;

      if (!this.track || this.cards.length === 0) return;

      this.dataset.initialized = 'true';
      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.update.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.previousButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    handleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.update();
      });
    }

    handleKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.move(event.key === 'ArrowRight' ? 1 : -1);
    }

    handleBlockSelect(event) {
      const card = event.target.closest?.('[data-fcs-card]');
      if (!card || !this.contains(card)) return;
      this.track.scrollTo({ left: card.offsetLeft, behavior: 'auto' });
    }

    move(direction) {
      const step = this.getStep();
      if (!step) return;
      this.track.scrollBy({
        left: direction * step,
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    getStep() {
      if (this.cards.length > 1) {
        return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
      }
      return this.cards[0]?.offsetWidth || 0;
    }

    getCurrentIndex() {
      const currentPosition = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - currentPosition);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    update() {
      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const hasOverflow = maximumScroll > 1;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= maximumScroll - 1;
      const progress = hasOverflow ? Math.min(1, Math.max(0, this.track.scrollLeft / maximumScroll)) : 1;
      const currentIndex = this.getCurrentIndex();

      this.dataset.hasOverflow = String(hasOverflow);

      if (this.previousButton) {
        this.previousButton.disabled = !hasOverflow || atStart;
        this.previousButton.setAttribute('aria-disabled', String(!hasOverflow || atStart));
      }

      if (this.nextButton) {
        this.nextButton.disabled = !hasOverflow || atEnd;
        this.nextButton.setAttribute('aria-disabled', String(!hasOverflow || atEnd));
      }

      if (this.progress) {
        this.progress.style.transform = `scaleX(${progress})`;
      }

      if (this.status) {
        const itemLabel = this.dataset.itemLabel || 'Item';
        const ofLabel = this.dataset.ofLabel || 'of';
        this.status.textContent = `${itemLabel} ${currentIndex + 1} ${ofLabel} ${this.cards.length}`;
      }
    }
  }

  customElements.define('finch-customer-slider', FinchCustomerSlider);
}
